import { useState } from 'react'
import type {
  BulkCreateRelationshipRequest,
  BulkCreateResponse,
  CaseEndpoint,
} from '../../../types/case-container'

interface UseBulkCreateLinkModalArgs {
  sourceEndpoint: CaseEndpoint
  targetIds: string[]
  endpoints: CaseEndpoint[]
  onCreate: (data: BulkCreateRelationshipRequest) => Promise<BulkCreateResponse | null>
}

export interface BulkCreateLinkModalState {
  connectionType: string
  setConnectionType: React.Dispatch<React.SetStateAction<string>>
  status: string
  setStatus: React.Dispatch<React.SetStateAction<string>>
  label: string
  setLabel: React.Dispatch<React.SetStateAction<string>>
  investigationNotes: string
  setInvestigationNotes: React.Dispatch<React.SetStateAction<string>>
  bidirectional: boolean
  setBidirectional: React.Dispatch<React.SetStateAction<boolean>>
  result: BulkCreateResponse | null
  validTargetIds: string[]
  targetEndpoints: CaseEndpoint[]
  totalRelationships: number
  isOpenQuestion: boolean
  handleSubmit: (e: React.FormEvent) => Promise<void>
}

export function useBulkCreateLinkModal(args: UseBulkCreateLinkModalArgs): BulkCreateLinkModalState {
  const { sourceEndpoint, targetIds, endpoints, onCreate } = args
  const [connectionType, setConnectionType] = useState('network_connection')
  const [status, setStatus] = useState('suspected')
  const [label, setLabel] = useState('')
  const [investigationNotes, setInvestigationNotes] = useState('')
  const [bidirectional, setBidirectional] = useState(false)
  const [result, setResult] = useState<BulkCreateResponse | null>(null)

  const validTargetIds = targetIds.filter((id) => id !== sourceEndpoint.id)
  const targetEndpoints: CaseEndpoint[] = []
  for (const id of validTargetIds) {
    const ep = endpoints.find((endpoint) => endpoint.id === id)
    if (ep) targetEndpoints.push(ep)
  }
  const totalRelationships = bidirectional ? validTargetIds.length * 2 : validTargetIds.length
  const isOpenQuestion = status === 'open-question'

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (validTargetIds.length === 0) return
    const request: BulkCreateRelationshipRequest = {
      sourceEndpointId: sourceEndpoint.id,
      targetEndpointIds: validTargetIds,
      connectionType,
      status,
      createBidirectional: bidirectional,
    }
    if (label) request.label = label
    if (investigationNotes) request.investigationNotes = investigationNotes
    const createResult = await onCreate(request)
    if (createResult) setResult(createResult)
  }

  return {
    connectionType,
    setConnectionType,
    status,
    setStatus,
    label,
    setLabel,
    investigationNotes,
    setInvestigationNotes,
    bidirectional,
    setBidirectional,
    result,
    validTargetIds,
    targetEndpoints,
    totalRelationships,
    isOpenQuestion,
    handleSubmit,
  }
}
